// HistoryItem (job page round): one thing that happened, in a record's history (a job's, a customer's,
// a timesheet's): when, a glyph for its kind, who did what in a sentence (names in bold), then what to
// know under it: a detail line, a change (was → now), the words of a text or a note on a mark, and
// anything else the row carries (the call's player, "See them" for photos).
// - A pinned note sits above the day groups, with no time, saying it's pinned.
// - Jumped to (a stop on the job's strip, a search): shaded with a heavier line on its left, and it takes
//   the focus so a screen reader reads it.
// - Round 2, opens in place (Amir 26 Oct, pages/job-history-option): with onToggle, the whole row (its
//   time, kind, words and detail line) is one button with aria-expanded and aria-controls, Area's
//   chevron at its end turned down when open, the row shaded on hover; open, it carries the ink mark
//   down its left edge and its entry (`expanded`) sits under its words. Escape inside an open row
//   closes it and puts the focus back on its row. A row with nothing more to show has no button. The
//   disclosure is RecordRow's too (row-disclosure.tsx).
// Reads only --hist-*.
import { forwardRef, type ReactNode } from 'react';
import { Glyph, type GlyphName } from '../Glyph/Glyph.tsx';
import { DisclosureChev, DisclosureEntry, useDisclosure } from '../row-disclosure.tsx';
import './HistoryItem.css';

export type HistoryKind =
  'call' | 'text' | 'note' | 'visit' | 'book' | 'photo' | 'form' | 'money' | 'estimate' | 'parts' | 'edit';

const GLYPH: Record<HistoryKind, GlyphName> = {
  call: 'phone',
  text: 'message',
  note: 'note',
  visit: 'pin',
  book: 'calendar',
  photo: 'camera',
  form: 'form',
  money: 'money',
  estimate: 'estimate',
  parts: 'parts',
  edit: 'pencil',
};
/** What each glyph is, for screen readers. */
export const KIND_WORDS: Record<HistoryKind, string> = {
  call: 'A call',
  text: 'A text',
  note: 'A note',
  visit: 'A visit',
  book: 'A booking',
  photo: 'Photos',
  form: 'A form',
  money: 'Money',
  estimate: 'An estimate',
  parts: 'Parts',
  edit: 'A change',
};

export interface HistoryItemProps {
  kind: HistoryKind;
  /** "8:20 a.m."; none for a pinned note. */
  time?: string;
  /** Who did what: "<b>Dana</b> texted Jordan". */
  children: ReactNode;
  detail?: ReactNode;
  /** A change: what it was, what it is now. */
  diff?: [string, string];
  /** The words of a text, a note, a call's summary. */
  quote?: string;
  /** Anything else it carries: the call's player, "See them". */
  extra?: ReactNode;
  isPinned?: boolean;
  /** Jumped to from the strip or a search. */
  isHit?: boolean;
  id?: string;
  /** Round 2: its head opens its detail (a side panel), so every row can be read in full. */
  onOpen?: () => void;
  /** Opens in place: the row is a disclosure, its entry under it. */
  onToggle?: () => void;
  isOpen?: boolean;
  /** The open row's entry (EntryDetail, placement inline). */
  expanded?: ReactNode;
}

export const HistoryItem = forwardRef<HTMLDivElement, HistoryItemProps>(function HistoryItem(
  {
    kind,
    time,
    children,
    detail,
    diff,
    quote,
    extra,
    isPinned,
    isHit,
    id,
    onOpen,
    onToggle,
    isOpen,
    expanded,
  },
  ref,
) {
  const { head, onKeyDown } = useDisclosure(!!isOpen, onToggle);
  const changes = diff ? (
    <>
      <span className="fl-hist__sr">was </span>
      <s className="fl-hist__was">{diff[0]}</s> <span aria-hidden="true">→</span>{' '}
      <span className="fl-hist__sr">now </span>
      {diff[1]}
    </>
  ) : null;
  if (onToggle) {
    const entryId = `${id ?? kind}-entry`;
    const open = !!isOpen;
    return (
      <div
        ref={ref}
        id={id}
        className="fl-hist"
        data-toggle=""
        data-open={open ? '' : undefined}
        data-hit={isHit ? '' : undefined}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <button
          ref={head}
          type="button"
          className="fl-hist__row"
          aria-expanded={open}
          aria-controls={entryId}
          onClick={onToggle}
        >
          <span className="fl-hist__time">{time}</span>
          <span className="fl-hist__kind" role="img" aria-label={KIND_WORDS[kind]}>
            <Glyph name={GLYPH[kind]} className="fl-hist__glyph" />
          </span>
          <span className="fl-hist__body">
            <span className="fl-hist__head">{children}</span>
            {detail ? <span className="fl-hist__detail">{detail}</span> : null}
            {changes ? <span className="fl-hist__detail">{changes}</span> : null}
          </span>
          <DisclosureChev className="fl-hist__chev" open={open} />
        </button>
        {quote ? <blockquote className="fl-hist__quote fl-hist__under">{quote}</blockquote> : null}
        {extra ? <div className="fl-hist__extra fl-hist__under">{extra}</div> : null}
        <DisclosureEntry className="fl-hist__entry" id={entryId} label={labelOf(children)} open={open}>
          {expanded}
        </DisclosureEntry>
      </div>
    );
  }
  return (
    <div
      ref={ref}
      id={id}
      className="fl-hist"
      data-pinned={isPinned ? '' : undefined}
      data-hit={isHit ? '' : undefined}
      tabIndex={-1}
    >
      <span className="fl-hist__time">{time}</span>
      <span className="fl-hist__kind" role="img" aria-label={KIND_WORDS[kind]}>
        <Glyph name={GLYPH[kind]} className="fl-hist__glyph" />
      </span>
      <div className="fl-hist__body">
        <p className="fl-hist__head">
          {onOpen ? (
            <button type="button" className="fl-hist__open" aria-haspopup="dialog" onClick={onOpen}>
              {children}
            </button>
          ) : (
            children
          )}
          {isPinned ? <span className="fl-hist__pin"> · pinned</span> : null}
        </p>
        {detail ? <div className="fl-hist__detail">{detail}</div> : null}
        {changes ? <p className="fl-hist__detail">{changes}</p> : null}
        {quote ? <blockquote className="fl-hist__quote">{quote}</blockquote> : null}
        {extra ? <div className="fl-hist__extra">{extra}</div> : null}
      </div>
    </div>
  );
});

/** The row's words as plain text, to name its entry ("Jordan paid $3,322.00"). */
function labelOf(n: ReactNode): string {
  if (typeof n === 'string' || typeof n === 'number') return String(n);
  if (Array.isArray(n)) return n.map(labelOf).join('');
  if (n && typeof n === 'object' && 'props' in n)
    return labelOf((n as { props: { children?: ReactNode } }).props.children);
  return '';
}
